import {
  ArrowLeft,
  ArrowRight,
  Calculator,
  ClipboardCheck,
  FileText,
  LoaderCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  WalletCards,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  applySettlementAdjustment,
  applySettlementManualReview,
  createSettlementDraft,
  createSettlementFeeRule,
  evaluateSettlementEligibility,
  generateSettlementStatement,
  getSettlement,
  listSettlementEligibility,
  listSettlementFeeRuleHistory,
  listSettlementFeeRules,
  listSettlements,
  settlementStatementUrl,
  transitionSettlement,
  updateSettlementFeeRule,
  type SettlementClientOption,
  type SettlementDetail,
  type SettlementEligibility,
  type SettlementFeeRule,
  type SettlementFeeRuleVersion,
  type SettlementInvoiceDraft,
  type SettlementLine,
  type SettlementMetrics,
  type SettlementPermissions,
  type SettlementRecord,
} from "../../lib/arRecoverySettlementsApi";
import {
  SETTLEMENT_BILLING_HANDOFF_BOUNDARY,
  SETTLEMENT_FINANCIAL_BOUNDARY,
  SETTLEMENT_WORKSPACE_TABS,
  basisPointsToPercent,
  canSelectDraftRow,
  centsToDollarsInput,
  clientHasApplicableFeeRule,
  createEmptyFeeRuleForm,
  dollarsToCents,
  draftRowSelectionLockedCurrency,
  emptyAgeTier,
  feeRuleModuleFields,
  feeRulePayloadsFromForm,
  FEE_RULE_REQUIRED_MESSAGE,
  lineFeeDisplay,
  normalizeSettlementWorkspaceTab,
  percentToBasisPoints,
  previewSettlementAdjustment,
  previewSettlementDraft,
  settlementActionAvailability,
  settlementApprovalBlockers,
  settlementReturnHref,
  targetSettlementLine,
  validateSettlementDraftSelection,
  type SettlementFeeRuleFormState,
  type SettlementWorkspaceTab,
} from "../../lib/arRecoverySettlementSlice1b";
import {
  REPLACEMENT_FEE_POLICY,
  SETTLEMENT_REVERSAL_BANNER,
  settlementLineage,
  settlementReversalBanner,
  type SettlementLineageView,
  type SettlementReversalBanner,
} from "../../lib/arRecoverySettlementSlice1c";
import {
  beginSettlementScope,
  commitSettlementScope,
  createSettlementRequestGuard,
  resolveSettlementScope,
  type SettlementScopedPanels,
} from "../../lib/arRecoverySettlementScope";

type ReasonKind = "text" | "qualifying" | "feeRate" | "explanation" | "adjustment";

type ReasonDialogState = {
  title: string;
  detail: string;
  actionLabel: string;
  kind: ReasonKind;
  defaultReason?: string;
  defaultAmount?: string;
  onSubmit: (input: { reason: string; amountCents?: number | null; feeRateBasisPoints?: number | null; explanation?: string | null; direction?: "increase" | "decrease" }) => Promise<void>;
};

const EMPTY_METRICS: SettlementMetrics = {
  settlementCount: 0,
  grossQualifyingRecoveryCents: 0,
  recoveryFeeCents: 0,
  totalClientInvoiceCents: 0,
  draftSettlements: 0,
  awaitingApproval: 0,
  approvedSettlements: 0,
  exceptionSettlements: 0,
  eligiblePayments: 0,
  pendingReview: 0,
  eligibilityExceptions: 0,
  evaluatedQualifyingCents: 0,
  invoiceDrafts: 0,
  sentInvoices: 0,
  paidInvoices: 0,
  overdueInvoices: 0,
};

const EMPTY_PERMISSIONS: SettlementPermissions = {
  viewQueue: false,
  evaluateEligibility: false,
  createDraft: false,
  manualReview: false,
  submitReview: false,
  approve: false,
  reject: false,
  correct: false,
  manageFeeRules: false,
  viewClientSafe: false,
  exportReport: false,
};

type Panels = SettlementScopedPanels<
  SettlementEligibility,
  SettlementLine,
  SettlementInvoiceDraft,
  SettlementDetail["statementVersions"][number],
  SettlementDetail["actions"][number],
  SettlementDetail["audit"][number],
  SettlementDetail["versions"][number]
>;

export function ARRecoverySettlementCenter() {
  const [searchParams] = useSearchParams();
  const requestGuard = useRef(createSettlementRequestGuard());
  const [settlements, setSettlements] = useState<SettlementRecord[]>([]);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [clients, setClients] = useState<SettlementClientOption[]>([]);
  const [selected, setSelected] = useState<SettlementDetail | null>(null);
  const [activeSettlementId, setActiveSettlementId] = useState("");
  const [panels, setPanels] = useState<Panels>(beginSettlementScope("", 0));
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("updated-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageCount, setPageCount] = useState(1);
  const [tab, setTab] = useState<SettlementWorkspaceTab>("summary");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [draftOpen, setDraftOpen] = useState(false);
  const [feeRulesOpen, setFeeRulesOpen] = useState(false);
  const [reasonDialog, setReasonDialog] = useState<ReasonDialogState | null>(null);
  const [selectedLineId, setSelectedLineId] = useState("");
  const [selectedEligibilityId, setSelectedEligibilityId] = useState("");

  const accountId = searchParams.get("accountId") || "";
  const clientIdFilter = searchParams.get("clientId") || "";
  const placementId = searchParams.get("placementId") || "";
  const returnRoute = searchParams.get("return");
  const returnHref = settlementReturnHref(returnRoute);

  async function refreshQueue(preferredSettlementId?: string) {
    setLoading(true);
    setError("");
    try {
      const result = await listSettlements({
        search: search.trim(),
        status,
        sort,
        page,
        pageSize,
        accountId: accountId || undefined,
        clientId: clientIdFilter || undefined,
        placementId: placementId || undefined,
      });
      setSettlements(result.settlements);
      setMetrics(result.metrics);
      setPermissions(result.permissions);
      setClients(result.clients || []);
      setCurrentUserId(result.currentUserId || "");
      setPageCount(Math.max(1, result.pagination.pageCount));
      const nextId = preferredSettlementId || activeSettlementId || result.settlements[0]?.settlementId;
      if (nextId) await selectSettlement(nextId, false);
      else {
        setSelected(null);
        setActiveSettlementId("");
        setPanels(beginSettlementScope("", 0));
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Settlement queue could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  async function selectSettlement(settlementId: string, showErrors = true) {
    const { requestId } = requestGuard.current.next(settlementId);
    setActiveSettlementId(settlementId);
    setSelectedLineId("");
    setSelectedEligibilityId("");
    setReasonDialog(null);
    setPanels(beginSettlementScope(settlementId, requestId));
    try {
      const detail = await getSettlement(settlementId);
      if (requestGuard.current.peek() !== requestId) return;
      setSelected(detail);
      setPermissions(detail.permissions);
      setCurrentUserId(detail.currentUserId || currentUserId);
      setPanels((current) => commitSettlementScope(current, settlementId, requestId, {
        eligibility: detail.eligibility || [],
        lines: detail.lines || [],
        invoiceDraft: detail.invoiceDraft,
        statementVersions: detail.statementVersions || [],
        actions: detail.actions || [],
        audit: detail.audit || [],
        versions: detail.versions || [],
        reversalBanner: settlementReversalBanner(detail.settlement),
        lineage: settlementLineage(detail.settlement),
      }));
    } catch (cause) {
      if (showErrors && requestGuard.current.peek() === requestId) {
        setError(cause instanceof Error ? cause.message : "Settlement detail could not be loaded.");
      }
    }
  }

  async function runMutation(action: () => Promise<unknown>, success: string) {
    setWorking(true);
    setMessage("");
    setError("");
    try {
      await action();
      setMessage(success);
      await refreshQueue(activeSettlementId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Settlement action could not be completed.");
    } finally {
      setWorking(false);
    }
  }

  useEffect(() => {
    void refreshQueue();
    // Queue refresh is intentionally driven by committed controls and workbench query context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status, sort, accountId, clientIdFilter, placementId]);

  const scoped = resolveSettlementScope(panels, activeSettlementId);
  const selectedSettlement = selected?.settlement.settlementId === activeSettlementId ? selected.settlement : null;
  const selectedDetail = selected?.settlement.settlementId === activeSettlementId ? selected : null;

  return (
    <section className="settlement-center-enterprise mx-auto grid w-full max-w-[100rem] gap-4" aria-label="Settlement Center">
      <header className="overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_18px_44px_rgba(10,37,64,.08)]">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[.18em] text-[#14B8A6]">Canonical Settlement Operations</p>
            <h2 className="mt-1 font-display text-3xl font-bold tracking-[-.03em] text-[#0A2540]">Settlement Center</h2>
            <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-[#475569]">{SETTLEMENT_FINANCIAL_BOUNDARY}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {returnHref ? (
              <Link className="settlement-button settlement-button-secondary" to={returnHref}>
                <ArrowLeft className="size-4" aria-hidden="true" />
                Return to Account Recovery
              </Link>
            ) : null}
            <button className="settlement-button settlement-button-secondary" type="button" onClick={() => void refreshQueue()} disabled={loading}>
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              Refresh
            </button>
            <button
              className="settlement-button settlement-button-secondary"
              type="button"
              disabled={!permissions.evaluateEligibility || working}
              onClick={() => runMutation(() => evaluateSettlementEligibility(), "Settlement eligibility evaluated.")}
            >
              <ClipboardCheck className="size-4" aria-hidden="true" />
              Evaluate Eligibility
            </button>
            <button
              className="settlement-button settlement-button-secondary"
              type="button"
              disabled={!permissions.viewQueue || working}
              onClick={() => setFeeRulesOpen(true)}
            >
              <SlidersHorizontal className="size-4" aria-hidden="true" />
              Fee Rules
            </button>
            <button
              className="settlement-button settlement-button-primary"
              type="button"
              disabled={!permissions.createDraft || working}
              onClick={() => setDraftOpen(true)}
            >
              <Calculator className="size-4" aria-hidden="true" />
              Create Draft
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px bg-[#BFEAFF] sm:grid-cols-3 xl:grid-cols-6">
          <SettlementMetric label="Eligible Recovery" value={metrics.eligiblePayments.toLocaleString()} note={money(metrics.evaluatedQualifyingCents)} />
          <SettlementMetric label="Pending Review" value={metrics.pendingReview.toLocaleString()} note="Eligibility decisions" />
          <SettlementMetric label="Drafts" value={metrics.draftSettlements.toLocaleString()} note="Operator drafts" />
          <SettlementMetric label="Awaiting Approval" value={metrics.awaitingApproval.toLocaleString()} note="Separation of duties" />
          <SettlementMetric label="Approved Fees" value={metrics.approvedSettlements.toLocaleString()} note={money(metrics.recoveryFeeCents)} />
          <SettlementMetric label="Ready for Billing" value={metrics.invoiceDrafts.toLocaleString()} note="Billing handoff snapshots" />
        </div>
      </header>

      {message ? <SettlementNotice tone="success" onClose={() => setMessage("")}>{message}</SettlementNotice> : null}
      {error ? <SettlementNotice tone="error" onClose={() => setError("")}>{error}</SettlementNotice> : null}

      <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(24rem,38%)_minmax(0,62%)]">
        <article className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_16px_38px_rgba(10,37,64,.07)]">
          <div className="border-b border-[#BFEAFF] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Settlement Queue</p>
                <h3 className="mt-1 text-lg font-black text-[#0A2540]">Client settlement work</h3>
              </div>
              <button type="button" className="settlement-icon-button" aria-label="Refresh settlement queue" onClick={() => void refreshQueue()} disabled={loading}>
                <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              </button>
            </div>
            <form
              className="mt-3 grid gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                setPage(1);
                void refreshQueue();
              }}
            >
              <label className="relative block">
                <span className="sr-only">Search settlements</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
                <input className="settlement-input pl-10" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search settlement, client, period, or status" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select className="settlement-input" aria-label="Settlement status" value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}>
                  <option value="">All statuses</option>
                  <option value="DRAFT">Draft</option>
                  <option value="AWAITING_APPROVAL">Awaiting approval</option>
                  <option value="APPROVED">Approved</option>
                  <option value="RETURNED">Returned</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="VOIDED">Voided</option>
                  <option value="REVERSED">Reversed</option>
                </select>
                <select className="settlement-input" aria-label="Sort settlements" value={sort} onChange={(event) => { setPage(1); setSort(event.target.value); }}>
                  <option value="updated-desc">Recently updated</option>
                  <option value="period-desc">Newest period</option>
                  <option value="period-asc">Oldest period</option>
                  <option value="amount-desc">Highest amount</option>
                  <option value="amount-asc">Lowest amount</option>
                  <option value="status-asc">Status</option>
                </select>
              </div>
            </form>
          </div>
          <div className="grid min-h-[24rem] content-start gap-2 bg-[#F4FBFF] p-3">
            {loading ? <SettlementLoading label="Loading canonical settlements" /> : null}
            {!loading && settlements.length === 0 ? (
              <SettlementEmpty icon={WalletCards} title="No settlements found" detail="Evaluate approved Payment Center records, then create a controlled settlement draft." />
            ) : null}
            {settlements.map((settlement) => (
              <button
                key={settlement.settlementId}
                type="button"
                className={`grid min-w-0 gap-2 rounded-2xl border p-3 text-left transition focus-ring ${
                  activeSettlementId === settlement.settlementId
                    ? "border-[#14B8A6] bg-white shadow-[0_10px_24px_rgba(20,184,166,.14)]"
                    : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"
                }`}
                onClick={() => void selectSettlement(settlement.settlementId)}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-black text-[#0A2540]">{settlement.clientName || "Client"}</p>
                    <p className="mt-0.5 truncate text-xs font-bold text-[#475569]">{settlement.settlementNumber} · {settlement.lineCount} lines</p>
                  </div>
                  <p className="shrink-0 text-base font-black text-[#0A2540]">{money(settlement.totalClientInvoiceCents)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <SettlementStatus value={settlement.settlementStatus} />
                  <SettlementStatus value={settlement.approvalStatus} />
                </div>
                <div className="flex items-center justify-between gap-3 text-[11px] font-bold text-[#64748B]">
                  <span>{settlement.settlementPeriodStart} to {settlement.settlementPeriodEnd}</span>
                  <span>v{settlement.version}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-[#BFEAFF] px-4 py-3 text-xs font-bold text-[#475569]">
            <button className="settlement-page-button" type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous settlement page">
              <ArrowLeft className="size-4" aria-hidden="true" />
            </button>
            <div className="flex min-w-0 items-center gap-2">
              <span className="whitespace-nowrap">Page {page} of {pageCount}</span>
              <label className="flex items-center gap-1.5 whitespace-nowrap">
                <span>Rows</span>
                <select
                  className="rounded-lg border border-[#BFEAFF] bg-white px-2 py-1 text-xs font-bold text-[#0A2540] focus-ring"
                  aria-label="Settlements per page"
                  value={pageSize}
                  onChange={(event) => {
                    setPage(1);
                    setPageSize(Number(event.target.value));
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
            <button className="settlement-page-button" type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)} aria-label="Next settlement page">
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          </div>
        </article>

        <article className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_16px_38px_rgba(10,37,64,.07)]">
          {!selectedSettlement || !selectedDetail ? (
            <div className="grid min-h-[42rem] place-items-center p-8">
              <SettlementEmpty icon={ShieldCheck} title="Select a settlement" detail="Choose a queue record to inspect eligibility, fee calculation, adjustments, statement, billing handoff, and audit history." />
            </div>
          ) : (
            <>
              <SettlementCommandHeader
                settlement={selectedSettlement}
                reversalBanner={scoped.reversalBanner}
                lineage={scoped.lineage}
                onSelectSettlement={(id) => void selectSettlement(id)}
              />
              <nav className="flex max-w-full gap-1 overflow-x-auto border-b border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2" aria-label="Settlement workspace tabs">
                {SETTLEMENT_WORKSPACE_TABS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl px-3 text-xs font-black transition focus-ring ${
                      tab === item.key ? "bg-[#0A2540] text-white" : "text-[#0A2540] hover:bg-white"
                    }`}
                    onClick={() => setTab(normalizeSettlementWorkspaceTab(item.key))}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
              <div className="min-w-0 p-4 sm:p-5">
                {tab === "summary" ? (
                  <SettlementSummary
                    detail={selectedDetail}
                    currentUserId={currentUserId}
                    disabled={working}
                    reversalBanner={scoped.reversalBanner}
                    lineage={scoped.lineage}
                    onAsk={(dialog) => setReasonDialog(dialog)}
                    onSelectSettlement={(id) => void selectSettlement(id)}
                  />
                ) : null}
                {tab === "eligibility" ? (
                  <SettlementEligibilityPanel
                    settlementId={selectedSettlement.settlementId}
                    expectedVersion={selectedSettlement.version}
                    eligibility={scoped.eligibility}
                    selectedEligibilityId={selectedEligibilityId}
                    onSelect={setSelectedEligibilityId}
                    disabled={working}
                    mutable={settlementActionAvailability(selectedSettlement.settlementStatus).reviewSources}
                    permissions={selectedDetail.permissions}
                    onAsk={(dialog) => setReasonDialog(dialog)}
                  />
                ) : null}
                {tab === "fee-calculation" ? (
                  <SettlementFeeCalculationPanel
                    settlementId={selectedSettlement.settlementId}
                    expectedVersion={selectedSettlement.version}
                    lines={scoped.lines}
                    selectedLineId={selectedLineId}
                    onSelect={setSelectedLineId}
                    disabled={working}
                    mutable={settlementActionAvailability(selectedSettlement.settlementStatus).adjustFinancials}
                    permissions={selectedDetail.permissions}
                    onAsk={(dialog) => setReasonDialog(dialog)}
                  />
                ) : null}
                {tab === "adjustments" ? (
                  <SettlementAdjustmentsPanel
                    detail={selectedDetail}
                    actions={scoped.actions}
                    disabled={working}
                    onAsk={(dialog) => setReasonDialog(dialog)}
                  />
                ) : null}
                {tab === "statement" ? (
                  <SettlementStatementPanel
                    detail={selectedDetail}
                    statementVersions={scoped.statementVersions}
                    disabled={working}
                    onAsk={(dialog) => setReasonDialog(dialog)}
                  />
                ) : null}
                {tab === "billing-handoff" ? (
                  <SettlementBillingHandoffPanel
                    invoice={scoped.invoiceDraft}
                    settlement={selectedSettlement}
                    statement={scoped.statementVersions.find((item) => item.isCurrent) || scoped.statementVersions[0] || null}
                  />
                ) : null}
                {tab === "history" ? (
                  <SettlementHistory
                    settlement={selectedSettlement}
                    actions={scoped.actions}
                    audit={selectedDetail.permissions.manualReview ? scoped.audit : []}
                    versions={scoped.versions}
                    lineage={scoped.lineage}
                    canViewAudit={selectedDetail.permissions.manualReview}
                    onSelectSettlement={(id) => void selectSettlement(id)}
                  />
                ) : null}
              </div>
            </>
          )}
        </article>
      </div>

      {draftOpen ? (
        <SettlementDraftDialog
          clients={clients}
          accountId={accountId}
          placementId={placementId}
          permissions={permissions}
          disabled={working}
          onClose={() => setDraftOpen(false)}
          onCreate={(draft) => runMutation(async () => {
            const created = await createSettlementDraft(draft);
            setDraftOpen(false);
            await selectSettlement(created.settlement.settlementId);
          }, "Settlement draft created from selected recovery records.")}
        />
      ) : null}
      {feeRulesOpen ? (
        <SettlementFeeRulesDialog
          clients={clients}
          permissions={permissions}
          disabled={working}
          onClose={() => setFeeRulesOpen(false)}
          onNotice={setMessage}
          onError={setError}
        />
      ) : null}
      {reasonDialog ? (
        <SettlementReasonDialog
          dialog={reasonDialog}
          disabled={working}
          onClose={() => setReasonDialog(null)}
          onWorking={setWorking}
          onError={setError}
          onComplete={async (success) => {
            setReasonDialog(null);
            setMessage(success);
            await refreshQueue(activeSettlementId);
          }}
        />
      ) : null}

      <style>{SETTLEMENT_CENTER_STYLE}</style>
    </section>
  );
}

function SettlementDraftDialog({
  clients,
  accountId,
  placementId,
  permissions,
  disabled,
  onClose,
  onCreate,
}: {
  clients: SettlementClientOption[];
  accountId?: string;
  placementId?: string;
  permissions: SettlementPermissions;
  disabled: boolean;
  onClose: () => void;
  onCreate: (draft: { clientId: string; settlementPeriodStart: string; settlementPeriodEnd: string; eligibilityIds: string[]; reason: string }) => void;
}) {
  const [clientId, setClientId] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState<SettlementEligibility[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [feeRules, setFeeRules] = useState<SettlementFeeRule[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    void listSettlementFeeRules().then((result) => setFeeRules(result.feeRules)).catch(() => setFeeRules([]));
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setRows([]);
    if (!clientId || !periodStart || !periodEnd) return;
    let cancelled = false;
    setLoadingRows(true);
    void listSettlementEligibility({
      clientId,
      periodStart,
      periodEnd,
      accountId: accountId || undefined,
      placementId: placementId || undefined,
      classification: "ELIGIBLE",
      limit: 100,
    }).then((result) => {
      if (cancelled) return;
      setRows(result.eligibility);
    }).catch((cause) => {
      if (!cancelled) setLocalError(cause instanceof Error ? cause.message : "Eligibility could not be loaded.");
    }).finally(() => {
      if (!cancelled) setLoadingRows(false);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, periodStart, periodEnd, accountId, placementId]);

  const lockedCurrency = draftRowSelectionLockedCurrency(rows, selectedIds);
  const selectedRows = rows.filter((row) => selectedIds.includes(row.eligibilityId));
  const preview = previewSettlementDraft(selectedRows);
  const validation = validateSettlementDraftSelection({ clientId, settlementPeriodStart: periodStart, settlementPeriodEnd: periodEnd, eligibilityIds: selectedIds, reason }, rows);
  const feeRuleMissing = Boolean(clientId) && !clientHasApplicableFeeRule(feeRules, clientId, lockedCurrency);

  return (
    <SettlementDialog title="Create Settlement Draft" detail="Select the client, settlement period, and specific eligible recovery records. Nothing is auto-selected." onClose={onClose} wide>
      {localError ? <p className="mb-3 text-sm font-bold text-[#991B1B]">{localError}</p> : null}
      <div className="grid gap-3 md:grid-cols-2">
        <label className="settlement-label">Client
          <select className="settlement-input" value={clientId} onChange={(event) => setClientId(event.target.value)} aria-label="Draft client">
            <option value="">Select a client</option>
            {clients.map((client) => <option key={client.clientId} value={client.clientId}>{client.clientName}</option>)}
          </select>
        </label>
        <label className="settlement-label">Currency lock
          <input className="settlement-input" value={lockedCurrency || "Selected rows must share one currency"} readOnly />
        </label>
        <label className="settlement-label">Settlement period start
          <input className="settlement-input" type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} />
        </label>
        <label className="settlement-label">Settlement period end
          <input className="settlement-input" type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} />
        </label>
      </div>
      {feeRuleMissing ? (
        <div className="mt-3 rounded-2xl border border-[#FDBA74] bg-[#FFF7ED] px-4 py-3 text-sm font-bold text-[#9A3412]">
          {FEE_RULE_REQUIRED_MESSAGE}. Eligibility cannot proceed until a governed client fee rule exists.
        </div>
      ) : null}
      <div className="mt-4 max-h-[22rem] overflow-auto rounded-2xl border border-[#BFEAFF]">
        {loadingRows ? <SettlementLoading label="Loading eligible recovery records" /> : (
          <table className="w-full min-w-[72rem] text-left text-sm">
            <thead className="bg-[#F4FBFF] text-[11px] font-black uppercase tracking-[.12em] text-[#0A2540]">
              <tr>
                {["Select", "Payment/reference", "Account/customer", "Placement", "Invoice", "Payment date", "Currency", "Qualifying", "Classification", "Reason", "Fee rule", "Estimated fee"].map((heading) => (
                  <th key={heading} className="border-b border-[#BFEAFF] px-3 py-2">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const selectable = canSelectDraftRow(row, clientId, lockedCurrency, selectedIds);
                return (
                  <tr key={row.eligibilityId} className="border-b border-[#E0F2FE]">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.paymentReference || row.paymentId}`}
                        checked={selectedIds.includes(row.eligibilityId)}
                        disabled={!selectable && !selectedIds.includes(row.eligibilityId)}
                        onChange={(event) => setSelectedIds((current) => event.target.checked ? [...current, row.eligibilityId] : current.filter((id) => id !== row.eligibilityId))}
                      />
                    </td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.paymentReference || row.paymentId}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.accountNumber || row.accountId || row.customerName || row.customerId || "—"}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.placementId || "—"}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.invoiceNumber || row.invoiceId || "—"}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.paymentDate || "—"}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.currency}</td>
                    <td className="px-3 py-3 font-black text-[#0A2540]">{money(row.qualifyingAmountCents)}</td>
                    <td className="px-3 py-3"><SettlementStatus value={row.classification} /></td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.reasonCode}</td>
                    <td className="px-3 py-3 font-semibold text-[#475569]">{row.feeRuleName || row.feeRuleId || FEE_RULE_REQUIRED_MESSAGE}</td>
                    <td className="px-3 py-3 font-black text-[#0A2540]">{row.estimatedFeeCents == null ? "—" : money(row.estimatedFeeCents)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <SettlementDataCard label="Selected recovery count" value={String(preview.selectedRecoveryCount)} note="Explicitly chosen rows" />
        <SettlementDataCard label="Gross qualifying recovery" value={money(preview.grossQualifyingRecoveryCents)} note="Selected eligible amount" />
        <SettlementDataCard label="Excluded/non-qualifying" value={money(preview.excludedNonQualifyingRecoveryCents)} note="Where present on selected rows" />
        <SettlementDataCard label="Estimated recovery fee" value={money(preview.estimatedRecoveryFeeCents)} note="Governed calculator preview" />
        <SettlementDataCard label="Total ready for Settlement" value={money(preview.totalReadyForSettlementCents)} note="Adjustment available after draft" />
      </div>
      <label className="settlement-label mt-4 block">Required reason
        <textarea className="settlement-input min-h-24" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Explain why this Settlement draft is being created." />
      </label>
      {!validation.ok ? <p className="mt-2 text-sm font-bold text-[#991B1B]">{validation.errors[0]}</p> : null}
      <div className="mt-4 flex justify-end gap-2">
        <button className="settlement-button settlement-button-secondary" type="button" onClick={onClose}>Cancel</button>
        <button
          className="settlement-button settlement-button-primary"
          type="button"
          disabled={disabled || !permissions.createDraft || !validation.ok || feeRuleMissing}
          onClick={() => onCreate({ clientId, settlementPeriodStart: periodStart, settlementPeriodEnd: periodEnd, eligibilityIds: selectedIds, reason })}
        >
          Create Draft
        </button>
      </div>
    </SettlementDialog>
  );
}

function SettlementFeeRulesDialog({
  clients,
  permissions,
  disabled,
  onClose,
  onNotice,
  onError,
}: {
  clients: SettlementClientOption[];
  permissions: SettlementPermissions;
  disabled: boolean;
  onClose: () => void;
  onNotice: (value: string) => void;
  onError: (value: string) => void;
}) {
  const [rules, setRules] = useState<SettlementFeeRule[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [history, setHistory] = useState<SettlementFeeRuleVersion[]>([]);
  const [form, setForm] = useState<SettlementFeeRuleFormState>(createEmptyFeeRuleForm());
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const result = await listSettlementFeeRules();
      setRules(result.feeRules);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "Fee rules could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setHistory([]);
      return;
    }
    void listSettlementFeeRuleHistory(selectedId).then((result) => setHistory(result.versions)).catch(() => setHistory([]));
  }, [selectedId]);

  const modules = feeRuleModuleFields(form.ruleType);
  const selected = rules.find((rule) => rule.feeRuleId === selectedId);

  function loadRule(rule: SettlementFeeRule) {
    setSelectedId(rule.feeRuleId);
    setForm({
      ...createEmptyFeeRuleForm(),
      clientId: rule.clientId,
      placementId: rule.placementId || "",
      ruleName: rule.ruleName,
      ruleType: rule.ruleType === "FLAT" ? "FLAT" : rule.ruleType === "AGE_TIER" || rule.ruleType === "AGE_BASED" ? "AGE_TIER" : "PERCENTAGE",
      feeRatePercent: basisPointsToPercent(rule.feeRateBasisPoints),
      flatFee: centsToDollarsInput(rule.flatFeeCents),
      minimumFee: centsToDollarsInput(rule.minimumFeeCents),
      maximumFee: centsToDollarsInput(rule.maximumFeeCents),
      ageTiers: [{
        fromDays: rule.ageBucketFromDays == null ? "0" : String(rule.ageBucketFromDays),
        toDays: rule.ageBucketToDays == null ? "" : String(rule.ageBucketToDays),
        feeRatePercent: basisPointsToPercent(rule.feeRateBasisPoints),
        minimumFee: centsToDollarsInput(rule.minimumFeeCents),
        maximumFee: centsToDollarsInput(rule.maximumFeeCents),
      }],
      effectiveDate: rule.effectiveDate,
      expirationDate: rule.expirationDate || "",
      currency: rule.currency,
      status: rule.status === "INACTIVE" ? "INACTIVE" : "ACTIVE",
      changeReason: "",
    });
  }

  async function save() {
    const payload = feeRulePayloadsFromForm(form);
    if (!payload.ok) {
      onError(payload.errors[0] || "Fee rule is incomplete.");
      return;
    }
    try {
      if (selectedId && payload.payloads.length === 1) {
        await updateSettlementFeeRule(selectedId, { ...payload.payloads[0], expectedVersion: selected?.version } as Parameters<typeof updateSettlementFeeRule>[1]);
        onNotice("Fee rule version created.");
      } else {
        for (const item of payload.payloads) {
          await createSettlementFeeRule(item as Parameters<typeof createSettlementFeeRule>[0]);
        }
        onNotice(payload.payloads.length > 1 ? "Age-tier fee rules created." : "Fee rule created.");
      }
      setForm(createEmptyFeeRuleForm());
      setSelectedId("");
      await refresh();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "Fee rule could not be saved.");
    }
  }

  return (
    <SettlementDialog title="Fee Rules" detail="Pricing structures are optional modules. Unused percentage, flat, or age fields stay inactive. Editing creates a new governed version." onClose={onClose} wide>
      <div className="grid gap-4 xl:grid-cols-[minmax(18rem,34%)_minmax(0,66%)]">
        <div className="grid content-start gap-2">
          {loading ? <SettlementLoading label="Loading fee rules" /> : null}
          {!loading && rules.length === 0 ? <SettlementEmpty icon={SlidersHorizontal} title="No governed fee rules" detail="No default fee rule is created. Add a client rule before eligibility can proceed." /> : null}
          {rules.map((rule) => (
            <button key={rule.feeRuleId} type="button" className={`rounded-2xl border p-3 text-left ${selectedId === rule.feeRuleId ? "border-[#14B8A6] bg-[#F0FDFA]" : "border-[#BFEAFF] bg-white"}`} onClick={() => loadRule(rule)}>
              <p className="text-sm font-black text-[#0A2540]">{rule.ruleName}</p>
              <p className="mt-1 text-xs font-semibold text-[#475569]">{rule.clientName} · {rule.ruleType} · v{rule.version} · {rule.status}</p>
            </button>
          ))}
        </div>
        <div className="grid gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="settlement-label">Client
              <select className="settlement-input" value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })}>
                <option value="">Select a client</option>
                {clients.map((client) => <option key={client.clientId} value={client.clientId}>{client.clientName}</option>)}
              </select>
            </label>
            <label className="settlement-label">Optional placement
              <input className="settlement-input" value={form.placementId} onChange={(event) => setForm({ ...form, placementId: event.target.value })} />
            </label>
            <label className="settlement-label">Rule name
              <input className="settlement-input" value={form.ruleName} onChange={(event) => setForm({ ...form, ruleName: event.target.value })} />
            </label>
            <label className="settlement-label">Rule type
              <select className="settlement-input" value={form.ruleType} onChange={(event) => setForm({ ...form, ruleType: event.target.value as SettlementFeeRuleFormState["ruleType"] })}>
                <option value="PERCENTAGE">Percentage</option>
                <option value="FLAT">Flat fee</option>
                <option value="AGE_TIER">Age-based percentage</option>
              </select>
            </label>
            <label className="settlement-label">Effective date
              <input className="settlement-input" type="date" value={form.effectiveDate} onChange={(event) => setForm({ ...form, effectiveDate: event.target.value })} />
            </label>
            <label className="settlement-label">Expiration date optional
              <input className="settlement-input" type="date" value={form.expirationDate} onChange={(event) => setForm({ ...form, expirationDate: event.target.value })} />
            </label>
            <label className="settlement-label">Currency
              <input className="settlement-input" value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })} />
            </label>
            <label className="settlement-label">Status
              <select className="settlement-input" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as "ACTIVE" | "INACTIVE" })}>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </label>
          </div>

          {modules.percentageActive ? (
            <fieldset className="rounded-2xl border border-[#BFEAFF] p-4">
              <legend className="px-1 text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Percentage module</legend>
              <label className="settlement-label">Percentage rate
                <input className="settlement-input" inputMode="decimal" value={form.feeRatePercent} onChange={(event) => setForm({ ...form, feeRatePercent: event.target.value })} />
              </label>
            </fieldset>
          ) : null}
          {modules.flatActive ? (
            <fieldset className="rounded-2xl border border-[#BFEAFF] p-4">
              <legend className="px-1 text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Flat fee module</legend>
              <label className="settlement-label">Flat fee amount
                <input className="settlement-input" inputMode="decimal" value={form.flatFee} onChange={(event) => setForm({ ...form, flatFee: event.target.value })} />
              </label>
            </fieldset>
          ) : null}
          {modules.ageActive ? (
            <fieldset className="rounded-2xl border border-[#BFEAFF] p-4">
              <legend className="px-1 text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Age-based percentage module</legend>
              <p className="text-xs font-semibold text-[#475569]">Age uses the authoritative invoice_date to payment_date basis. Open-ended tiers leave To days blank.</p>
              {form.ageTiers.map((tier, index) => (
                <div key={`tier-${index}`} className="mt-3 grid gap-2 md:grid-cols-5">
                  <label className="settlement-label">From days<input className="settlement-input" value={tier.fromDays} onChange={(event) => setForm({ ...form, ageTiers: form.ageTiers.map((item, itemIndex) => itemIndex === index ? { ...item, fromDays: event.target.value } : item) })} /></label>
                  <label className="settlement-label">To days or open<input className="settlement-input" value={tier.toDays} placeholder="Open-ended" onChange={(event) => setForm({ ...form, ageTiers: form.ageTiers.map((item, itemIndex) => itemIndex === index ? { ...item, toDays: event.target.value } : item) })} /></label>
                  <label className="settlement-label">Percentage rate<input className="settlement-input" value={tier.feeRatePercent} onChange={(event) => setForm({ ...form, ageTiers: form.ageTiers.map((item, itemIndex) => itemIndex === index ? { ...item, feeRatePercent: event.target.value } : item) })} /></label>
                  <label className="settlement-label">Optional min<input className="settlement-input" value={tier.minimumFee} onChange={(event) => setForm({ ...form, ageTiers: form.ageTiers.map((item, itemIndex) => itemIndex === index ? { ...item, minimumFee: event.target.value } : item) })} /></label>
                  <label className="settlement-label">Optional max<input className="settlement-input" value={tier.maximumFee} onChange={(event) => setForm({ ...form, ageTiers: form.ageTiers.map((item, itemIndex) => itemIndex === index ? { ...item, maximumFee: event.target.value } : item) })} /></label>
                </div>
              ))}
              {!selectedId ? (
                <button className="settlement-button settlement-button-secondary mt-3" type="button" onClick={() => setForm({ ...form, ageTiers: [...form.ageTiers, emptyAgeTier()] })}>Add age tier</button>
              ) : null}
            </fieldset>
          ) : null}

          <fieldset className="rounded-2xl border border-dashed border-[#BFEAFF] p-4">
            <legend className="px-1 text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Optional min/max</legend>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="settlement-label">Minimum fee optional
                <input className="settlement-input" value={form.minimumFee} onChange={(event) => setForm({ ...form, minimumFee: event.target.value })} disabled={modules.ageActive} />
              </label>
              <label className="settlement-label">Maximum fee optional
                <input className="settlement-input" value={form.maximumFee} onChange={(event) => setForm({ ...form, maximumFee: event.target.value })} disabled={modules.ageActive} />
              </label>
            </div>
          </fieldset>

          <label className="settlement-label">Change reason required
            <textarea className="settlement-input min-h-20" value={form.changeReason} onChange={(event) => setForm({ ...form, changeReason: event.target.value })} />
          </label>
          {selected ? (
            <p className="text-xs font-semibold text-[#475569]">Current version {selected.version} · created by {selected.createdByName || "unknown"} · updated by {selected.updatedByName || "unknown"} · {selected.effectiveDate}{selected.expirationDate ? ` to ${selected.expirationDate}` : " onward"}</p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button className="settlement-button settlement-button-secondary" type="button" onClick={() => { setSelectedId(""); setForm(createEmptyFeeRuleForm()); }}>New rule</button>
            <button className="settlement-button settlement-button-primary" type="button" disabled={disabled || !permissions.manageFeeRules} onClick={() => void save()}>{selectedId ? "Save new version" : "Create fee rule"}</button>
          </div>
          {!permissions.manageFeeRules ? <p className="text-xs font-bold text-[#9A3412]">Fee-rule management is permission-gated. History remains visible.</p> : null}
          <div className="grid gap-2">
            {history.map((version) => (
              <div key={version.feeRuleVersionId} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                <p className="text-sm font-black text-[#0A2540]">Version {version.version} · {version.ruleType}</p>
                <p className="mt-1 text-xs font-semibold text-[#475569]">{version.createdAt} · {version.changeReason} · {version.effectiveDate}{version.expirationDate ? ` to ${version.expirationDate}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SettlementDialog>
  );
}

function SettlementSummary({
  detail,
  currentUserId,
  disabled,
  reversalBanner,
  lineage,
  onAsk,
  onSelectSettlement,
}: {
  detail: SettlementDetail;
  currentUserId: string;
  disabled: boolean;
  reversalBanner: SettlementReversalBanner | null;
  lineage: SettlementLineageView | null;
  onAsk: (dialog: ReasonDialogState) => void;
  onSelectSettlement: (settlementId: string) => void;
}) {
  const settlement = detail.settlement;
  const availability = settlementActionAvailability(settlement.settlementStatus, {
    replacementSettlementId: settlement.replacementSettlementId,
  });
  const blockers = settlementApprovalBlockers({
    currentUserId,
    preparedByUserId: settlement.preparedByUserId,
    lastFinancialModifierUserId: settlement.lastFinancialModifierUserId,
    feeOverriderUserId: settlement.feeOverriderUserId,
  });
  function workflow(action: "submit_review" | "return_correction" | "approve" | "reject" | "void" | "reverse" | "create_replacement", title: string) {
    onAsk({
      title,
      detail: action === "create_replacement"
        ? "Replacement creates a new DRAFT Settlement, revalidates sources, and recalculates fees. A reason is required."
        : "A real operator reason is required. The server remains authoritative.",
      actionLabel: title,
      kind: "text",
      onSubmit: async ({ reason }) => {
        await transitionSettlement(settlement.settlementId, { action, reason, expectedVersion: settlement.version });
      },
    });
  }
  return (
    <div className="grid gap-4">
      {reversalBanner && reversalBanner.settlementId === settlement.settlementId ? (
        <SettlementReversalBannerCard banner={reversalBanner} lineage={lineage} onSelectSettlement={onSelectSettlement} />
      ) : null}
      {lineage && lineage.settlementId === settlement.settlementId ? (
        <SettlementLineageCard lineage={lineage} onSelectSettlement={onSelectSettlement} />
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SettlementDataCard label="Settlement number" value={settlement.settlementNumber} note={`Version ${settlement.version}`} />
        <SettlementDataCard label="Client" value={settlement.clientName || settlement.clientId} note={settlement.currency} />
        <SettlementDataCard label="Period" value={`${settlement.settlementPeriodStart} to ${settlement.settlementPeriodEnd}`} note={settlement.settlementStatus} />
        <SettlementDataCard label="Approval status" value={settlement.approvalStatus.replaceAll("_", " ")} note={`Calculation v${settlement.calculationVersion}`} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SettlementDataCard label="Gross qualifying recovery" value={money(settlement.grossQualifyingRecoveryCents)} note="Approved eligible recovery" />
        <SettlementDataCard label="Excluded/non-qualifying recovery" value={money(settlement.excludedNonQualifyingRecoveryCents)} note="Not billed as recovery fee basis" />
        <SettlementDataCard label="Settlement-level adjustment" value={money(settlement.adjustmentCents)} note="Pre-approval financial adjustment" />
        <SettlementDataCard label="Net qualifying recovery" value={money(settlement.netQualifyingRecoveryCents)} note="Gross remains the net recovery basis" />
        <SettlementDataCard label="Recovery fee" value={money(settlement.recoveryFeeCents)} note="Governed calculator result" />
        <SettlementDataCard label="Total for Billing handoff" value={money(settlement.totalClientInvoiceCents)} note="Approved recovery fee plus adjustment" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <SettlementDataCard label="Preparer" value={settlement.preparedByName || "—"} note={settlement.preparedByUserId || ""} />
        <SettlementDataCard label="Last financial modifier" value={settlement.lastFinancialModifierName || "—"} note={settlement.lastFinancialModifierUserId || ""} />
        <SettlementDataCard label="Reviewer/submitted by" value={settlement.reviewedByName || "—"} note="Independent review" />
        <SettlementDataCard label="Approver" value={settlement.approvedByName || "—"} note={settlement.approvedAt || "Not approved"} />
        <SettlementDataCard label="Fee overrider" value={settlement.feeOverriderName || "—"} note={settlement.feeOverriderUserId ? "Override recorded" : "None"} />
        <SettlementDataCard label="Reversed by" value={settlement.reversedByName || "—"} note={settlement.reversedAt || "Not reversed"} />
      </div>
      <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <p className="text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Financial boundary</p>
        <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">{SETTLEMENT_FINANCIAL_BOUNDARY}</p>
        {settlement.settlementStatus === "REVERSED" ? <p className="mt-2 text-sm font-bold text-[#9A3412]">Ordinary financial edit controls are unavailable on a reversed Settlement. {REPLACEMENT_FEE_POLICY}</p> : null}
        {blockers.map((blocker) => (
          <p key={blocker.code} className="mt-2 text-sm font-bold text-[#9A3412]">{blocker.message}</p>
        ))}
        <div className="mt-4 flex flex-wrap gap-2">
          {availability.submitReview && detail.permissions.submitReview ? <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled} onClick={() => workflow("submit_review", "Submit for Review")}>Submit for Review</button> : null}
          {availability.returnCorrection && detail.permissions.correct ? <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled} onClick={() => workflow("return_correction", "Return for Correction")}>Return for Correction</button> : null}
          {availability.approve && detail.permissions.approve ? <button className="settlement-button settlement-button-primary" type="button" disabled={disabled || blockers.length > 0} onClick={() => workflow("approve", "Approve Settlement")}>Approve Settlement</button> : null}
          {availability.reject && detail.permissions.reject ? <button className="settlement-button settlement-button-danger" type="button" disabled={disabled} onClick={() => workflow("reject", "Reject Settlement")}>Reject Settlement</button> : null}
          {availability.voidDraft && detail.permissions.submitReview ? <button className="settlement-button settlement-button-danger" type="button" disabled={disabled} onClick={() => workflow("void", "Void Draft/Returned Settlement")}>Void Draft/Returned Settlement</button> : null}
          {availability.reverseApproved && detail.permissions.approve ? <button className="settlement-button settlement-button-danger" type="button" disabled={disabled} onClick={() => workflow("reverse", "Reverse Settlement")}>Reverse Settlement</button> : null}
          {availability.createReplacement && detail.permissions.createDraft ? <button className="settlement-button settlement-button-primary" type="button" disabled={disabled} onClick={() => workflow("create_replacement", "Create Replacement Settlement")}>Create Replacement Settlement</button> : null}
        </div>
      </div>
    </div>
  );
}

function SettlementEligibilityPanel({
  settlementId,
  expectedVersion,
  eligibility,
  selectedEligibilityId,
  onSelect,
  disabled,
  mutable,
  permissions,
  onAsk,
}: {
  settlementId: string;
  expectedVersion: number;
  eligibility: SettlementEligibility[];
  selectedEligibilityId: string;
  onSelect: (value: string) => void;
  disabled: boolean;
  mutable: boolean;
  permissions: SettlementPermissions;
  onAsk: (dialog: ReasonDialogState) => void;
}) {
  const selected = eligibility.find((item) => item.eligibilityId === selectedEligibilityId) || null;
  function run(action: "confirm_eligibility" | "mark_ineligible" | "pending_review" | "return_source_payment", title: string) {
    if (!selected) return;
    onAsk({
      title,
      detail: "Manual eligibility changes require a reason and do not mutate Payment Center.",
      actionLabel: title,
      kind: "text",
      onSubmit: async ({ reason }) => {
        await applySettlementManualReview(settlementId, {
          action,
          reason,
          expectedVersion,
          eligibilityId: selected.eligibilityId,
        });
      },
    });
  }
  return (
    <div className="grid gap-3">
      <SectionLead title="Eligibility" detail="This tab shows only source eligibility records belonging to the selected Settlement. Payment Center records are not mutated." />
      <div className="max-h-[32rem] min-w-0 overflow-auto rounded-2xl border border-[#BFEAFF]">
        <table className="w-full min-w-[72rem] text-left text-sm">
          <thead className="bg-[#F4FBFF] text-[11px] font-black uppercase tracking-[.12em] text-[#0A2540]">
            <tr>
              {["Payment ID/reference", "Allocation/invoice", "Customer/account", "Placement", "Qualifying", "Classification", "Reason", "Fee rule", "Currency", "Source status"].map((heading) => (
                <th key={heading} className="border-b border-[#BFEAFF] px-3 py-2">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {eligibility.map((item) => (
              <tr key={item.eligibilityId} className={`cursor-pointer border-b border-[#E0F2FE] ${selected?.eligibilityId === item.eligibilityId ? "bg-[#ECFEFF]" : ""}`} onClick={() => onSelect(item.eligibilityId)}>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.paymentReference || item.paymentId}</td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.invoiceNumber || item.allocationId || "Payment-level"}</td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.accountNumber || item.accountId || item.customerName || item.customerId || "—"}</td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.placementId || "—"}</td>
                <td className="px-3 py-3 font-black text-[#0A2540]">{money(item.qualifyingAmountCents)}</td>
                <td className="px-3 py-3"><SettlementStatus value={item.classification} /></td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.reasonCode}: {item.reasonDetail}</td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.feeRuleId ? `${item.feeRuleName || item.feeRuleId} v${item.feeRuleVersion || 1}` : FEE_RULE_REQUIRED_MESSAGE}</td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.currency}</td>
                <td className="px-3 py-3 font-semibold text-[#475569]">{item.sourceStatus || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <p className="text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Selected eligibility</p>
        <p className="mt-1 text-sm font-semibold text-[#475569]">{selected ? `${selected.paymentReference || selected.paymentId} · ${selected.classification}` : "Select a row to target eligibility actions."}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !mutable || !permissions.manualReview || !selected} onClick={() => run("confirm_eligibility", "Confirm Eligible")}>Confirm Eligible</button>
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !mutable || !permissions.manualReview || !selected} onClick={() => run("mark_ineligible", "Mark Ineligible")}>Mark Ineligible</button>
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !mutable || !permissions.manualReview || !selected} onClick={() => run("pending_review", "Pending Review")}>Pending Review</button>
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !mutable || !permissions.manualReview || !selected} onClick={() => run("return_source_payment", "Return Source Payment")}>Return Source Payment</button>
        </div>
      </div>
    </div>
  );
}

function SettlementFeeCalculationPanel({
  settlementId,
  expectedVersion,
  lines,
  selectedLineId,
  onSelect,
  disabled,
  mutable,
  permissions,
  onAsk,
}: {
  settlementId: string;
  expectedVersion: number;
  lines: SettlementLine[];
  selectedLineId: string;
  onSelect: (value: string) => void;
  disabled: boolean;
  mutable: boolean;
  permissions: SettlementPermissions;
  onAsk: (dialog: ReasonDialogState) => void;
}) {
  const selected = targetSettlementLine(lines, selectedLineId);
  function run(action: "exclude_line" | "adjust_qualifying_amount" | "fee_override" | "add_explanation", title: string, kind: ReasonKind) {
    if (!selected) return;
    onAsk({
      title,
      detail: "This action targets the selected calculation line, not the first line in the Settlement.",
      actionLabel: title,
      kind,
      defaultAmount: kind === "qualifying" ? centsToDollarsInput(selected.qualifyingAmountCents) : kind === "feeRate" ? basisPointsToPercent(selected.feeRateBasisPoints) : "",
      onSubmit: async ({ reason, amountCents, feeRateBasisPoints, explanation }) => {
        await applySettlementManualReview(settlementId, {
          action,
          reason,
          expectedVersion,
          lineId: selected.lineId,
          qualifyingAmountCents: action === "adjust_qualifying_amount" ? amountCents : undefined,
          feeRateBasisPoints: action === "fee_override" ? feeRateBasisPoints : undefined,
          explanation: action === "add_explanation" ? explanation || reason : undefined,
        });
      },
    });
  }
  return (
    <div className="grid gap-3">
      <SectionLead title="Fee Calculation" detail="Each line is calculated by the governed Settlement engine. React does not recompute fees." />
      <div className="max-h-[32rem] min-w-0 overflow-auto rounded-2xl border border-[#BFEAFF]">
        <table className="w-full min-w-[90rem] text-left text-sm">
          <thead className="bg-[#F4FBFF] text-[11px] font-black uppercase tracking-[.12em] text-[#0A2540]">
            <tr>
              {["Source Payment", "Invoice", "Placement", "Qualifying", "Excluded", "Fee rule", "Version", "Type", "Rate/amount/age", "Min applied", "Max applied", "Calculated fee", "Explanation", "Adjustment", "Final line fee"].map((heading) => (
                <th key={heading} className="border-b border-[#BFEAFF] px-3 py-2">{heading}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const display = lineFeeDisplay(line);
              return (
                <tr key={line.lineId} className={`cursor-pointer border-b border-[#E0F2FE] ${selected?.lineId === line.lineId ? "bg-[#ECFEFF]" : ""}`} onClick={() => onSelect(line.lineId)}>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.paymentId}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.invoiceNumber || line.invoiceId || "Unassigned"}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.placementId || "—"}</td>
                  <td className="px-3 py-3 font-black text-[#0A2540]">{money(line.qualifyingAmountCents)}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{money(line.nonQualifyingAmountCents)}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.feeRuleId || "—"}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.feeRuleVersion}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.feeRuleType || display.type}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{display.type === "FLAT" ? money(Number(display.value || 0)) : `${basisPointsToPercent(Number(display.value || 0))}% ${display.label}`}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.minApplied ? "Yes" : "No"}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.maxApplied ? "Yes" : "No"}</td>
                  <td className="px-3 py-3 font-black text-[#0A2540]">{money(line.calculatedFeeCents)}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{line.calculationExplanation}</td>
                  <td className="px-3 py-3 font-semibold text-[#475569]">{money(line.adjustmentCents)}</td>
                  <td className="px-3 py-3 font-black text-[#0A2540]">{money(line.calculatedFeeCents + line.adjustmentCents)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <p className="text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Selected line</p>
        <p className="mt-1 text-sm font-semibold text-[#475569]">{selected ? `${selected.lineId} · ${selected.paymentId}` : "Select a calculation line. Actions never default to lines[0]."}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !permissions.manualReview || !mutable || !selected} onClick={() => run("exclude_line", "Exclude Line", "text")}>Exclude Line</button>
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !permissions.correct || !mutable || !selected} onClick={() => run("adjust_qualifying_amount", "Adjust Qualifying Amount", "qualifying")}>Adjust Qualifying Amount</button>
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !permissions.manualReview || !mutable || !selected} onClick={() => run("fee_override", "Override Fee Rate/Amount", "feeRate")}>Override Fee Rate/Amount</button>
          <button className="settlement-button settlement-button-secondary" type="button" disabled={disabled || !permissions.manualReview || !selected} onClick={() => run("add_explanation", "Add Explanation", "explanation")}>Add Explanation</button>
        </div>
      </div>
    </div>
  );
}

function SettlementAdjustmentsPanel({
  detail,
  actions,
  disabled,
  onAsk,
}: {
  detail: SettlementDetail;
  actions: SettlementDetail["actions"];
  disabled: boolean;
  onAsk: (dialog: ReasonDialogState) => void;
}) {
  const availability = settlementActionAvailability(detail.settlement.settlementStatus);
  const history = actions.filter((item) => item.actionType === "SettlementAdjusted");
  return (
    <div className="grid gap-4">
      <SectionLead title="Adjustments" detail="Settlement-level financial adjustment is allowed only before approval and only with the correct permission. The backend remains fail-closed after APPROVED." />
      <div className="grid gap-3 sm:grid-cols-3">
        <SettlementDataCard label="Current adjustment" value={money(detail.settlement.adjustmentCents)} note="Settlement-level" />
        <SettlementDataCard label="Recovery fee" value={money(detail.settlement.recoveryFeeCents)} note="Before this adjustment preview" />
        <SettlementDataCard label="Total for Billing handoff" value={money(detail.settlement.totalClientInvoiceCents)} note="Fee plus adjustment" />
      </div>
      {availability.adjustFinancials && detail.permissions.correct ? (
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <button
            className="settlement-button settlement-button-primary"
            type="button"
            disabled={disabled}
            onClick={() => onAsk({
              title: "Apply settlement-level adjustment",
              detail: "Enter a positive amount, choose increase or decrease, and provide a required reason.",
              actionLabel: "Apply Adjustment",
              kind: "adjustment",
              onSubmit: async ({ reason, amountCents, direction }) => {
                const preview = previewSettlementAdjustment(detail.settlement.adjustmentCents, amountCents || 0, direction || "increase");
                await applySettlementAdjustment(detail.settlement.settlementId, {
                  adjustmentCents: preview.adjustmentCents,
                  reason,
                  expectedVersion: detail.settlement.version,
                });
              },
            })}
          >
            Apply Adjustment
          </button>
        </div>
      ) : (
        <p className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3 text-sm font-bold text-[#475569]">
          Adjustment action is hidden after approval, on reversed or voided Settlements, or when the operator lacks correction permission.
        </p>
      )}
      <div className="grid gap-2">
        {history.map((item) => (
          <div key={item.actionId} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-sm font-black text-[#0A2540]">{item.actorName} recorded a settlement adjustment</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{item.occurredAt} · {item.reason}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettlementStatementPanel({
  detail,
  statementVersions,
  disabled,
  onAsk,
}: {
  detail: SettlementDetail;
  statementVersions: SettlementDetail["statementVersions"];
  disabled: boolean;
  onAsk: (dialog: ReasonDialogState) => void;
}) {
  const current = statementVersions.find((item) => item.isCurrent) || statementVersions[0];
  const canGenerate = (detail.permissions.correct || detail.permissions.approve) && detail.settlement.settlementStatus === "APPROVED";
  const canRetrieve = detail.permissions.viewClientSafe;
  return (
    <div className="grid gap-4">
      <SectionLead title="Settlement Statement" detail="Approved-only client-safe Settlement statements. This is not a Billing invoice presentation." />
      <p className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3 text-sm font-semibold text-[#475569]">
        Statements belong to {detail.settlement.settlementNumber} only. Reversal preserves this historical statement. A replacement Settlement generates its own statement after approval.
      </p>
      <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-black text-[#0A2540]">{current ? current.statementNumber : "No statement generated"}</p>
            <p className="mt-1 text-xs font-bold text-[#475569]">
              {current ? `Current v${current.version} from Settlement v${current.sourceSettlementVersion} generated by ${current.generatedByName} at ${current.generatedAt}` : "Generate after approval to create the first client-safe version."}
            </p>
          </div>
          {canGenerate ? (
            <button
              className="settlement-button settlement-button-primary"
              type="button"
              disabled={disabled}
              onClick={() => onAsk({
                title: "Generate Statement",
                detail: "Statement generation requires an operator reason and is limited to approved Settlements.",
                actionLabel: "Generate Statement",
                kind: "text",
                onSubmit: async ({ reason }) => {
                  await generateSettlementStatement(detail.settlement.settlementId, { reason, expectedVersion: detail.settlement.version });
                },
              })}
            >
              Generate Statement
            </button>
          ) : null}
        </div>
        {current && canRetrieve ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <a className="settlement-button settlement-button-secondary" href={settlementStatementUrl(detail.settlement.settlementId, current.statementVersionId)} target="_blank" rel="noreferrer">Preview</a>
            <a className="settlement-button settlement-button-secondary" href={settlementStatementUrl(detail.settlement.settlementId, current.statementVersionId, { download: true })}>Download</a>
            <button className="settlement-button settlement-button-secondary" type="button" onClick={() => window.open(settlementStatementUrl(detail.settlement.settlementId, current.statementVersionId), "_blank", "noopener,noreferrer")?.print()}>Print</button>
          </div>
        ) : null}
        {current && !canRetrieve ? <p className="mt-3 text-sm font-bold text-[#9A3412]">Statement retrieval is permission-gated.</p> : null}
      </div>
      <div className="grid gap-2">
        {statementVersions.map((item) => (
          <div key={item.statementVersionId} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-sm font-black text-[#0A2540]">{item.statementNumber} {item.isCurrent ? "Current" : "Previous"}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">Statement v{item.version} from Settlement v{item.sourceSettlementVersion} generated by {item.generatedByName} at {item.generatedAt}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettlementBillingHandoffPanel({
  invoice,
  settlement,
  statement,
}: {
  invoice: SettlementInvoiceDraft | null;
  settlement: SettlementRecord;
  statement: SettlementDetail["statementVersions"][number] | null;
}) {
  return (
    <div className="grid gap-3">
      <SectionLead title="Billing Handoff" detail={SETTLEMENT_BILLING_HANDOFF_BOUNDARY} />
      {!invoice ? <SettlementEmpty icon={FileText} title="No billing handoff snapshot" detail="Approve a Settlement to create the governed invoice-draft snapshot. Billing Center owns later lifecycle." /> : (
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <SettlementDataCard label="Invoice draft number" value={invoice.invoiceNumber} note="Read-only snapshot" />
            <SettlementDataCard label="Settlement number" value={settlement.settlementNumber} note={`Settlement v${settlement.version}`} />
            <SettlementDataCard label="Approved recovery fee" value={money(settlement.recoveryFeeCents)} note={invoice.currency} />
            <SettlementDataCard label="Total for billing" value={money(invoice.totalDueCents)} note="Handoff amount" />
            <SettlementDataCard label="Currency" value={invoice.currency} note={invoice.invoiceDate} />
            <SettlementDataCard label="Created date" value={invoice.updatedAt} note={`Billing handoff status: ${invoice.invoiceStatus}`} />
            <SettlementDataCard label="Statement reference" value={statement?.statementNumber || "Not generated"} note={statement ? `Statement v${statement.version}` : "No statement version"} />
          </div>
          <p className="mt-4 text-sm font-semibold leading-6 text-[#475569]">{SETTLEMENT_BILLING_HANDOFF_BOUNDARY}</p>
          <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">This handoff snapshot belongs to {settlement.settlementNumber} only. Reversal preserves it. A replacement Settlement creates a new invoice-draft ID after approval. Billing lifecycle is not mutated here.</p>
        </div>
      )}
    </div>
  );
}

function SettlementHistory({
  settlement,
  actions,
  audit,
  versions,
  lineage,
  canViewAudit,
  onSelectSettlement,
}: {
  settlement: SettlementRecord;
  actions: SettlementDetail["actions"];
  audit: SettlementDetail["audit"];
  versions: SettlementDetail["versions"];
  lineage: SettlementLineageView | null;
  canViewAudit: boolean;
  onSelectSettlement: (settlementId: string) => void;
}) {
  return (
    <div className="grid gap-3">
      <SectionLead title="Audit & Versions" detail="Actions, financial changes, fee overrides, eligibility actions, approvals, reversal/replacement lineage, and version identity already stored for this Settlement." />
      {lineage && lineage.settlementId === settlement.settlementId ? (
        <SettlementLineageCard lineage={lineage} onSelectSettlement={onSelectSettlement} />
      ) : null}
      <div className="grid gap-2">
        {actions.map((action) => (
          <div key={action.actionId} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-sm font-black text-[#0A2540]">{action.actionType}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{action.actorName} · {action.actorRole} · {action.occurredAt} · {action.reason || "No reason recorded"} · {action.previousStatus || "—"} → {action.newStatus || "—"}</p>
          </div>
        ))}
        {versions.map((version) => (
          <div key={version.versionId} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
            <p className="text-sm font-black text-[#0A2540]">Settlement version {version.version} · calculation {version.calculationVersion}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{version.actorName} · {version.createdAt} · {version.reason}</p>
          </div>
        ))}
        {canViewAudit ? audit.map((entry) => (
          <div key={entry.auditId} className="rounded-2xl border border-[#BFEAFF] bg-white p-3">
            <p className="text-sm font-black text-[#0A2540]">{entry.operation} · {entry.outcome}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{entry.actorName} · v{entry.previousVersion ?? "—"} → v{entry.resultingVersion ?? "—"} · {entry.occurredAt} · {entry.reason || "No reason recorded"}</p>
          </div>
        )) : <p className="text-xs font-bold text-[#9A3412]">Detailed audit entries are permission-gated.</p>}
      </div>
    </div>
  );
}

function SettlementReasonDialog({
  dialog,
  disabled,
  onClose,
  onWorking,
  onError,
  onComplete,
}: {
  dialog: ReasonDialogState;
  disabled: boolean;
  onClose: () => void;
  onWorking: (value: boolean) => void;
  onError: (value: string) => void;
  onComplete: (success: string) => Promise<void>;
}) {
  const [reason, setReason] = useState(dialog.defaultReason || "");
  const [amount, setAmount] = useState(dialog.defaultAmount || "");
  const [direction, setDirection] = useState<"increase" | "decrease">("increase");
  async function submit() {
    if (!reason.trim()) {
      onError("A reason is required.");
      return;
    }
    onWorking(true);
    try {
      await dialog.onSubmit({
        reason: reason.trim(),
        amountCents: dialog.kind === "qualifying" || dialog.kind === "adjustment" ? dollarsToCents(amount) : null,
        feeRateBasisPoints: dialog.kind === "feeRate" ? percentToBasisPoints(amount) : null,
        explanation: dialog.kind === "explanation" ? reason.trim() : null,
        direction,
      });
      await onComplete(`${dialog.actionLabel} recorded.`);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : "Settlement action could not be completed.");
    } finally {
      onWorking(false);
    }
  }
  return (
    <SettlementDialog title={dialog.title} detail={dialog.detail} onClose={onClose}>
      {dialog.kind === "qualifying" || dialog.kind === "feeRate" || dialog.kind === "adjustment" ? (
        <label className="settlement-label">{dialog.kind === "feeRate" ? "Fee rate percent" : "Amount"}
          <input className="settlement-input" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </label>
      ) : null}
      {dialog.kind === "adjustment" ? (
        <label className="settlement-label mt-3 block">Direction
          <select className="settlement-input" value={direction} onChange={(event) => setDirection(event.target.value as "increase" | "decrease")}>
            <option value="increase">Increase</option>
            <option value="decrease">Decrease</option>
          </select>
        </label>
      ) : null}
      <label className="settlement-label mt-3 block">Required reason
        <textarea className="settlement-input min-h-28" value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>
      <div className="mt-4 flex justify-end gap-2">
        <button className="settlement-button settlement-button-secondary" type="button" onClick={onClose}>Cancel</button>
        <button className="settlement-button settlement-button-primary" type="button" disabled={disabled || !reason.trim()} onClick={() => void submit()}>{dialog.actionLabel}</button>
      </div>
    </SettlementDialog>
  );
}

function SettlementDialog({ title, detail, onClose, wide = false, children }: { title: string; detail: string; onClose: () => void; wide?: boolean; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center overflow-y-auto bg-[#02072C]/70 p-3 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={title}>
      <section className={`my-auto w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_30px_90px_rgba(2,7,44,.35)] ${wide ? "max-w-6xl" : "max-w-3xl"}`}>
        <header className="flex items-start justify-between gap-4 border-b border-[#BFEAFF] px-5 py-4">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Settlement Center</p>
            <h2 className="mt-1 text-xl font-black text-[#0A2540]">{title}</h2>
            <p className="mt-1 text-sm font-semibold text-[#475569]">{detail}</p>
          </div>
          <button className="settlement-icon-button" type="button" aria-label={`Close ${title}`} onClick={onClose}><X className="size-4" aria-hidden="true" /></button>
        </header>
        <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-5">{children}</div>
      </section>
    </div>
  );
}

function SettlementCommandHeader({
  settlement,
  reversalBanner,
  lineage,
  onSelectSettlement,
}: {
  settlement: SettlementRecord;
  reversalBanner: SettlementReversalBanner | null;
  lineage: SettlementLineageView | null;
  onSelectSettlement: (settlementId: string) => void;
}) {
  return (
    <header className="grid gap-3 border-b border-[#BFEAFF] p-4">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Selected Settlement</p>
            <SettlementStatus value={settlement.settlementStatus} />
            <SettlementStatus value={settlement.approvalStatus} />
          </div>
          <h3 className="mt-1 truncate text-xl font-black text-[#0A2540]">{settlement.clientName || "Client Settlement"}</h3>
          <p className="mt-1 break-words text-sm font-bold text-[#475569]">
            {settlement.settlementNumber} · {settlement.settlementPeriodStart} to {settlement.settlementPeriodEnd} · v{settlement.version}
          </p>
        </div>
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3 text-left sm:text-right">
          <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Total for Billing handoff</p>
          <p className="mt-1 text-2xl font-black text-[#0A2540]">{money(settlement.totalClientInvoiceCents)}</p>
          <p className="text-xs font-bold text-[#475569]">{settlement.currency}</p>
        </div>
      </div>
      {reversalBanner && reversalBanner.settlementId === settlement.settlementId ? (
        <SettlementReversalBannerCard banner={reversalBanner} lineage={lineage} onSelectSettlement={onSelectSettlement} />
      ) : null}
      {lineage && lineage.settlementId === settlement.settlementId && settlement.settlementStatus !== "REVERSED" ? (
        <SettlementLineageCard lineage={lineage} onSelectSettlement={onSelectSettlement} />
      ) : null}
    </header>
  );
}

function SettlementReversalBannerCard({
  banner,
  lineage,
  onSelectSettlement,
}: {
  banner: SettlementReversalBanner;
  lineage: SettlementLineageView | null;
  onSelectSettlement: (settlementId: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-4" role="status">
      <p className="text-sm font-black uppercase tracking-[.14em] text-[#991B1B]">{SETTLEMENT_REVERSAL_BANNER}</p>
      <p className="mt-2 text-sm font-semibold text-[#7F1D1D]">
        Reversed {banner.reversedAt || "at an unknown time"} by {banner.reversedByName}. Reason: {banner.reversalReason || "No reason recorded"}.
      </p>
      <p className="mt-1 text-sm font-semibold text-[#7F1D1D]">
        Replacement status: {banner.replacementStatus === "created" ? `created${banner.replacementSettlementNumber ? ` (${banner.replacementSettlementNumber})` : ""}` : "not created"}.
      </p>
      {lineage?.replacementSettlementId ? (
        <button className="settlement-button settlement-button-secondary mt-3" type="button" onClick={() => onSelectSettlement(lineage.replacementSettlementId!)}>
          {lineage.replacementLabel || "Open replacement Settlement"}
        </button>
      ) : null}
    </div>
  );
}

function SettlementLineageCard({
  lineage,
  onSelectSettlement,
}: {
  lineage: SettlementLineageView;
  onSelectSettlement: (settlementId: string) => void;
}) {
  return (
    <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
      <p className="text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">Settlement lineage</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {lineage.originalSettlementId && lineage.originalLabel ? (
          <button className="settlement-button settlement-button-secondary" type="button" onClick={() => onSelectSettlement(lineage.originalSettlementId!)}>
            {lineage.originalLabel}
          </button>
        ) : null}
        {lineage.replacementSettlementId && lineage.replacementLabel ? (
          <button className="settlement-button settlement-button-secondary" type="button" onClick={() => onSelectSettlement(lineage.replacementSettlementId!)}>
            {lineage.replacementLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SettlementMetric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="min-w-0 bg-white px-4 py-3">
      <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">{label}</p>
      <p className="mt-1 text-xl font-black text-[#0A2540]">{value}</p>
      <p className="mt-0.5 truncate text-[11px] font-semibold text-[#64748B]">{note}</p>
    </div>
  );
}

function SettlementDataCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-2xl border border-[#BFEAFF] bg-white p-4">
      <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">{label}</p>
      <p className="mt-1 text-xl font-black text-[#0A2540]">{value}</p>
      <p className="mt-1 text-xs font-semibold text-[#64748B]">{note}</p>
    </div>
  );
}

function SectionLead({ title, detail }: { title: string; detail: string }) {
  return (
    <div>
      <p className="text-xs font-black uppercase tracking-[.14em] text-[#14B8A6]">{title}</p>
      <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{detail}</p>
    </div>
  );
}

function SettlementStatus({ value }: { value: string }) {
  return <span className="inline-flex items-center rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-2.5 py-1 text-[10px] font-black uppercase tracking-[.1em] text-[#0A2540]">{value.replaceAll("_", " ")}</span>;
}

function SettlementNotice({ tone, children, onClose }: { tone: "success" | "error"; children: ReactNode; onClose: () => void }) {
  return (
    <div className={`flex items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-sm font-bold ${tone === "success" ? "border-[#14B8A6] bg-[#ECFEFF] text-[#0A2540]" : "border-[#FCA5A5] bg-[#FEF2F2] text-[#991B1B]"}`}>
      <span>{children}</span>
      <button type="button" onClick={onClose} className="font-black underline">Dismiss</button>
    </div>
  );
}

function SettlementLoading({ label }: { label: string }) {
  return <div className="flex items-center justify-center gap-2 rounded-2xl border border-[#BFEAFF] bg-white p-6 text-sm font-bold text-[#475569]"><LoaderCircle className="size-4 animate-spin text-[#14B8A6]" aria-hidden="true" />{label}</div>;
}

function SettlementEmpty({ icon: Icon, title, detail }: { icon: typeof WalletCards; title: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#BFEAFF] bg-white p-6 text-center">
      <Icon className="mx-auto size-8 text-[#14B8A6]" aria-hidden="true" />
      <h4 className="mt-3 text-base font-black text-[#0A2540]">{title}</h4>
      <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{detail}</p>
    </div>
  );
}

function money(cents = 0) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format((cents || 0) / 100);
}

const SETTLEMENT_CENTER_STYLE = `
  .settlement-center-enterprise .settlement-button {
    display: inline-flex;
    min-height: 2.5rem;
    align-items: center;
    justify-content: center;
    gap: .5rem;
    border-radius: 999px;
    padding: .6rem 1rem;
    font-size: .72rem;
    font-weight: 900;
    letter-spacing: .12em;
    text-transform: uppercase;
    transition: border-color .16s ease, background .16s ease, opacity .16s ease;
  }
  .settlement-center-enterprise .settlement-button:disabled {
    cursor: not-allowed;
    opacity: .45;
  }
  .settlement-center-enterprise .settlement-button-primary {
    border: 1px solid #14B8A6;
    background: #0A2540;
    color: white;
  }
  .settlement-center-enterprise .settlement-button-secondary {
    border: 1px solid #14B8A6;
    background: white;
    color: #0A2540;
  }
  .settlement-center-enterprise .settlement-button-danger {
    border: 1px solid #FCA5A5;
    background: #FEF2F2;
    color: #991B1B;
  }
  .settlement-center-enterprise .settlement-input {
    min-width: 0;
    width: 100%;
    border-radius: .9rem;
    border: 1px solid #BFEAFF;
    background: white;
    padding: .72rem .85rem;
    font-size: .875rem;
    font-weight: 700;
    color: #0A2540;
  }
  .settlement-center-enterprise .settlement-label {
    display: grid;
    gap: .35rem;
    font-size: .68rem;
    font-weight: 900;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: #0A2540;
  }
  .settlement-center-enterprise .settlement-icon-button,
  .settlement-center-enterprise .settlement-page-button {
    display: inline-flex;
    min-height: 2.25rem;
    min-width: 2.25rem;
    align-items: center;
    justify-content: center;
    border-radius: .85rem;
    border: 1px solid #BFEAFF;
    background: white;
    color: #0A2540;
  }
  .settlement-center-enterprise .settlement-page-button:disabled,
  .settlement-center-enterprise .settlement-icon-button:disabled {
    opacity: .45;
  }
`;
